import { useEffect, useLayoutEffect } from 'react';
import { useQuery } from '@tanstack/react-query';

/**
 * Bo giao dien theo tai khoan (Django employees.UiPreference + ui_theme.py).
 *
 * Gan `<html data-ui-theme="default|clickup|atino_dark" data-ui-app="<app>">`
 * theo trang dang xem: `per_app[app] ?? theme`. CSS cua tung bo o
 * `ui-themes.css`. App xac dinh theo doan dau URL — duong dan TS dung cung
 * prefix voi Django (nhanhvn, kol, cashflow, cash-qr, ...), trang khong thuoc
 * app nao (Cong ung dung, Ho so, Cai dat, tiktok, cskh...) → 'account'.
 */

export type UiThemeKey = 'default' | 'clickup' | 'atino_dark';

export interface UiPrefs {
  theme: string;
  per_app: Record<string, string>;
}

export const UI_THEMES: ReadonlyArray<UiThemeKey> = ['default', 'clickup', 'atino_dark'];
const VALID = new Set<string>(UI_THEMES);

/** `APPS` cua ui_theme.py (khoa = doan dau URL). */
export const UI_APP_KEYS: ReadonlyArray<string> = [
  'nhanhvn',
  'kol',
  'cashflow',
  'cash-qr',
  'disbursement',
  'hr',
  'voucher',
  'cleaning',
  'can-nang',
  'thht',
  'rbac',
  'auditlog',
  'account',
];
const APP_KEYS = new Set(UI_APP_KEYS);

export function appKeyForPath(path: string): string {
  const seg = (path || '/').replace(/^\/+|\/+$/g, '').split('/', 1)[0] ?? '';
  return APP_KEYS.has(seg) ? seg : 'account';
}

export function themeForPath(prefs: UiPrefs | undefined, path: string): UiThemeKey {
  if (!prefs) return 'default';
  const app = appKeyForPath(path);
  const t = prefs.per_app[app] ?? prefs.theme;
  return (VALID.has(t) ? t : 'default') as UiThemeKey;
}

export const UI_PREFS_KEY = ['ui-prefs'] as const;
const STORE_KEY = 'atino:ui-prefs';

/**
 * Trang cong khai (dang nhap, dang ky voucher, tra cuu bao, checklist nhan
 * vien) khong co phien: khong goi API — client chung se day ve /login khi 401.
 */
const PUBLIC_RE =
  /^\/(login|logout|403)(\/|$)|^\/voucher\/(register|claim|success)(\/|$)|^\/nhanhvn\/tra-cuu-bao(\/|$)|^\/cleaning\/(worker|check)(\/|$)|^\/cleaning\/zones\/\d+\/qr\/?$/;

function readCached(): UiPrefs | undefined {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    if (!raw) return undefined;
    const v = JSON.parse(raw) as UiPrefs;
    return v && typeof v.theme === 'string' && v.per_app && typeof v.per_app === 'object' ? v : undefined;
  } catch {
    return undefined;
  }
}

/** Fetch rieng (khong qua api client) de 401 khong ep chuyen trang. */
async function fetchPrefs(): Promise<UiPrefs> {
  const res = await fetch('/api/cai-dat/giao-dien/prefs', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) return { theme: 'default', per_app: {} };
  const v = (await res.json()) as UiPrefs;
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(v));
  } catch {
    /* khong luu duoc cung khong sao — lan sau doi API */
  }
  return v;
}

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&family=Be+Vietnam+Pro:wght@400;500;600;700;800&display=swap';

/** Chi tai font Nunito / Be Vietnam Pro khi that su dung bo ClickUp / Atino toi. */
export function ensureThemeFonts(): void {
  if (document.getElementById('ui-theme-fonts')) return;
  const link = document.createElement('link');
  link.id = 'ui-theme-fonts';
  link.rel = 'stylesheet';
  link.href = FONT_HREF;
  document.head.appendChild(link);
}

export function applyUiTheme(theme: UiThemeKey, app: string): void {
  const el = document.documentElement;
  if (theme !== 'default') ensureThemeFonts();
  if (el.dataset['uiTheme'] !== theme) el.dataset['uiTheme'] = theme;
  if (el.dataset['uiApp'] !== app) el.dataset['uiApp'] = app;
}

/**
 * Nap bo giao dien cua tai khoan MOT lan (react-query, cache vo han — trang
 * Cai dat giao dien invalidate sau khi luu) va ap lai moi lan doi trang.
 * Lan dau dung ban luu o localStorage de khong nhay mau truoc khi API tra ve.
 */
export function useUiTheme(pathname: string): void {
  const isPublic = PUBLIC_RE.test(pathname);
  const { data } = useQuery({
    queryKey: UI_PREFS_KEY,
    queryFn: fetchPrefs,
    enabled: !isPublic,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    placeholderData: readCached,
  });
  const prefs = isPublic ? undefined : data;
  const theme = themeForPath(prefs, pathname);
  const app = appKeyForPath(pathname);
  // Layout effect: doi mau truoc khi trinh duyet ve khung hinh moi.
  useLayoutEffect(() => {
    applyUiTheme(theme, app);
  }, [theme, app]);
  useEffect(() => {
    // Dang xuat / doi tai khoan: bo ban luu cu khi API tra mac dinh cho khach.
    if (isPublic && pathname.startsWith('/login')) {
      try {
        window.localStorage.removeItem(STORE_KEY);
      } catch {
        /* bo qua */
      }
    }
  }, [isPublic, pathname]);
}
